import { defineMessages, useIntl } from 'react-intl';
import {
  AutoSuggest,
  AutoSuggestListItem,
} from '@lifeomic/phc-web-toolkit/src/components/AutoSuggest';
import KnowledgeService, {
  GeneResponse,
} from '@lifeomic/phc-web-toolkit/src/services/KnowledgeService';
import React from 'react';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';

interface GeneTypeaheadResult extends GeneResponse {
  type?: 'MANUAL';
}

const knowledgeService = new KnowledgeService();

const messages = defineMessages({
  placeholder: {
    id: 'pages.genomics.common.genetypeahead.placeholder',
    defaultMessage: 'Search genes…',
  },
  manualEntry: {
    id: 'pages.genomics.common.genetypeahead.manualEntry',
    defaultMessage: 'Manual Entry',
  },
  hgnc: {
    id: 'pages.genomics.common.genetypeahead.hgnc',
    defaultMessage: 'HGNC',
  },
  commaSeperated: {
    id: 'pages.genomics.common.genetypeahead.commaSeperated',
    defaultMessage: 'Separate with a comma for multiples',
  },
});

interface GeneTypeaheadProps {
  allowManualEntry?: boolean;
  disabled?: boolean;
  filterBy?: string;
  inputErrorMessage?: string;
  onGeneAdd?: (gene: any) => any;
  selections?: Array<any>;
}

export const GeneTypeahead: React.FC<GeneTypeaheadProps> = ({
  allowManualEntry,
  disabled,
  filterBy,
  inputErrorMessage,
  onGeneAdd,
  selections,
}) => {
  const { formatMessage } = useIntl();

  const [responseGenes, setResponseGenes] = React.useState<
    Array<GeneTypeaheadResult>
  >([]);
  const [inputValue, setInputValue] = React.useState<string>('');

  const [getGenes, getGenesState] = useService(knowledgeService.getGenes);

  React.useEffect(() => {
    if (
      allowManualEntry &&
      inputValue &&
      getGenesState?.response?.data?.items?.length === 0
    ) {
      setResponseGenes([
        {
          gene: inputValue,
          id: null,
          type: 'MANUAL',
        },
      ]);
      return;
    }

    if (!getGenesState?.response?.data?.items) {
      setResponseGenes([]);
      return;
    }

    // We want to put their text entry at the top (always),
    // and render the suggestions below when manual entry
    // is enabled.  This is because they may not always want the HGNC entry
    // if their gene name is correct.
    let results = allowManualEntry
      ? [
          {
            gene: inputValue,
            id: null,
            type: 'MANUAL',
          },
          ...getGenesState.response.data.items,
        ]
      : getGenesState.response.data.items;

    if (selections?.length) {
      results = results.filter((item: any) => {
        return !selections.find(selection => {
          // In OE, the `filterBy` here is "searchValue".
          // There are cases where we have filters that return a boolean,
          // so we just skip over those to not bust the app
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          if (typeof selection[filterBy] === 'boolean') {
            return;
          }

          // In the case that selections are an array of gene strings
          if (typeof selection === 'string') {
            return item?.gene?.toLowerCase() === selection.toLowerCase();
          }

          return filterBy
            ? item?.gene?.toLowerCase() === selection[filterBy]?.toLowerCase()
            : item?.gene?.toLowerCase() === selection?.gene?.toLowerCase();
        });
      });
    }

    setResponseGenes(results);
  }, [allowManualEntry, filterBy, getGenesState, inputValue, selections]);

  const fetchGenesWithInputValue = async (inputValue: any) => {
    setInputValue(inputValue);
    await getGenes({ gene: inputValue });
  };

  const getSuggestionValue = (term: any) => {
    return term.gene;
  };

  const handleGeneClick = ({
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    suggestion,
  }) => {
    onGeneAdd?.(suggestion);
  };

  const renderSuggestion = (term: any, isHighlighted: any) => {
    return (
      <AutoSuggestListItem
        title={term.gene}
        subtitle={
          term.type === 'MANUAL'
            ? formatMessage(messages.manualEntry)
            : term.name && term.alias
            ? [term.name, term.alias.join(', ')]
            : formatMessage(messages.hgnc)
        }
        isHighlighted={isHighlighted}
      />
    );
  };

  return (
    <AutoSuggest
      disabled={disabled}
      fetchSuggestions={fetchGenesWithInputValue}
      getSuggestionValue={getSuggestionValue}
      inputErrorMessage={inputErrorMessage}
      inputPlaceholder={formatMessage(messages.placeholder)}
      suggestions={responseGenes}
      isLoading={getGenesState.isFetching}
      renderSuggestion={renderSuggestion}
      onSuggestionSelected={handleGeneClick}
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      inputHelperText={
        allowManualEntry ? formatMessage(messages.commaSeperated) : null
      }
    />
  );
};
